import {
  ACTIVE_MARKER_IMAGE_ID,
  ACTIVE_PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID,
  ACTIVE_PIN_ELEMENTARY_MIDDLE_IMAGE_ID,
  ACTIVE_PIN_ELEMENTARY_SCHOOL_IMAGE_ID,
  ACTIVE_PIN_HIGH_SCHOOL_IMAGE_ID,
  ACTIVE_PIN_K8_HIGH_IMAGE_ID,
  ACTIVE_PIN_K8_SCHOOL_IMAGE_ID,
  ACTIVE_PIN_MIDDLE_HIGH_IMAGE_ID,
  ACTIVE_PIN_MIDDLE_SCHOOL_IMAGE_ID,
  ACTIVE_PIN_SCHOOL_INACTIVE_IMAGE_ID,
  HOVER_MARKER_IMAGE_ID,
  HOVER_PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID,
  HOVER_PIN_ELEMENTARY_MIDDLE_IMAGE_ID,
  HOVER_PIN_ELEMENTARY_SCHOOL_IMAGE_ID,
  HOVER_PIN_HIGH_SCHOOL_IMAGE_ID,
  HOVER_PIN_K8_HIGH_IMAGE_ID,
  HOVER_PIN_K8_SCHOOL_IMAGE_ID,
  HOVER_PIN_MIDDLE_HIGH_IMAGE_ID,
  HOVER_PIN_MIDDLE_SCHOOL_IMAGE_ID,
  HOVER_PIN_SCHOOL_INACTIVE_IMAGE_ID,
  MARKER_IMAGE_ID,
  PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID,
  PIN_ELEMENTARY_MIDDLE_IMAGE_ID,
  PIN_ELEMENTARY_SCHOOL_IMAGE_ID,
  PIN_HIGH_SCHOOL_IMAGE_ID,
  PIN_K8_HIGH_IMAGE_ID,
  PIN_K8_SCHOOL_IMAGE_ID,
  PIN_MIDDLE_HIGH_IMAGE_ID,
  PIN_MIDDLE_SCHOOL_IMAGE_ID,
  PIN_SCHOOL_INACTIVE_IMAGE_ID,
} from "../constants";

import {
  schoolTypeContainedInTypesCsvExpression,
  schoolTypesCsvNonEmptyExpression,
  schoolTypesFirstTokenExpression,
} from "./school-types-csv";

import type { ExpressionSpecification } from "maplibre-gl";

export type SchoolPinsIconVariant = "normal" | "active" | "linked";

export interface SchoolPinsIconExpressionOptions {
  variant: SchoolPinsIconVariant;
  emptyTypesFallback?: "marker" | "inactive";
}

interface SchoolPinAssetIds {
  elementary: string;
  middle: string;
  high: string;
  k8: string;
  k8High: string;
  middleHigh: string;
  elementaryMiddleHigh: string;
  elementaryMiddle: string;
  inactive: string;
  marker: string;
}

const SCHOOL_PIN_ASSETS_BY_VARIANT: Record<SchoolPinsIconVariant, SchoolPinAssetIds> = {
  normal: {
    elementary: PIN_ELEMENTARY_SCHOOL_IMAGE_ID,
    middle: PIN_MIDDLE_SCHOOL_IMAGE_ID,
    high: PIN_HIGH_SCHOOL_IMAGE_ID,
    k8: PIN_K8_SCHOOL_IMAGE_ID,
    k8High: PIN_K8_HIGH_IMAGE_ID,
    middleHigh: PIN_MIDDLE_HIGH_IMAGE_ID,
    elementaryMiddleHigh: PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID,
    elementaryMiddle: PIN_ELEMENTARY_MIDDLE_IMAGE_ID,
    inactive: PIN_SCHOOL_INACTIVE_IMAGE_ID,
    marker: MARKER_IMAGE_ID,
  },
  active: {
    elementary: ACTIVE_PIN_ELEMENTARY_SCHOOL_IMAGE_ID,
    middle: ACTIVE_PIN_MIDDLE_SCHOOL_IMAGE_ID,
    high: ACTIVE_PIN_HIGH_SCHOOL_IMAGE_ID,
    k8: ACTIVE_PIN_K8_SCHOOL_IMAGE_ID,
    k8High: ACTIVE_PIN_K8_HIGH_IMAGE_ID,
    middleHigh: ACTIVE_PIN_MIDDLE_HIGH_IMAGE_ID,
    elementaryMiddleHigh: ACTIVE_PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID,
    elementaryMiddle: ACTIVE_PIN_ELEMENTARY_MIDDLE_IMAGE_ID,
    inactive: ACTIVE_PIN_SCHOOL_INACTIVE_IMAGE_ID,
    marker: ACTIVE_MARKER_IMAGE_ID,
  },
  linked: {
    elementary: HOVER_PIN_ELEMENTARY_SCHOOL_IMAGE_ID,
    middle: HOVER_PIN_MIDDLE_SCHOOL_IMAGE_ID,
    high: HOVER_PIN_HIGH_SCHOOL_IMAGE_ID,
    k8: HOVER_PIN_K8_SCHOOL_IMAGE_ID,
    k8High: HOVER_PIN_K8_HIGH_IMAGE_ID,
    middleHigh: HOVER_PIN_MIDDLE_HIGH_IMAGE_ID,
    elementaryMiddleHigh: HOVER_PIN_ELEMENTARY_MIDDLE_HIGH_IMAGE_ID,
    elementaryMiddle: HOVER_PIN_ELEMENTARY_MIDDLE_IMAGE_ID,
    inactive: HOVER_PIN_SCHOOL_INACTIVE_IMAGE_ID,
    marker: HOVER_MARKER_IMAGE_ID,
  },
};

function singleTypeMatchExpression(assets: SchoolPinAssetIds): ExpressionSpecification {
  return [
    "match",
    schoolTypesFirstTokenExpression,
    "ELEMENTARY",
    assets.elementary,
    "MIDDLE",
    assets.middle,
    "HIGH",
    assets.high,
    "K8",
    assets.k8,
    assets.inactive,
  ];
}

/**
 * Data-driven `icon-image` for school_pins MVT: multi-type combos (K8+HIGH, MIDDLE+HIGH, EM+H, EM), then first type in `types`.
 */
export function buildSchoolPinsIconExpression(
  options: SchoolPinsIconExpressionOptions,
): ExpressionSpecification {
  const { variant, emptyTypesFallback = "marker" } = options;
  const assets = SCHOOL_PIN_ASSETS_BY_VARIANT[variant];
  const emptyId = emptyTypesFallback === "inactive" ? assets.inactive : assets.marker;

  return [
    "case",
    ["has", "closedAt"],
    assets.inactive,
    schoolTypesCsvNonEmptyExpression,
    [
      "case",
      [
        "all",
        schoolTypeContainedInTypesCsvExpression("ELEMENTARY"),
        schoolTypeContainedInTypesCsvExpression("MIDDLE"),
        schoolTypeContainedInTypesCsvExpression("HIGH"),
      ],
      assets.elementaryMiddleHigh,
      [
        "all",
        schoolTypeContainedInTypesCsvExpression("K8"),
        schoolTypeContainedInTypesCsvExpression("HIGH"),
      ],
      assets.k8High,
      [
        "all",
        schoolTypeContainedInTypesCsvExpression("MIDDLE"),
        schoolTypeContainedInTypesCsvExpression("HIGH"),
      ],
      assets.middleHigh,
      [
        "all",
        schoolTypeContainedInTypesCsvExpression("ELEMENTARY"),
        schoolTypeContainedInTypesCsvExpression("MIDDLE"),
        ["!", schoolTypeContainedInTypesCsvExpression("HIGH")],
      ],
      assets.elementaryMiddle,
      singleTypeMatchExpression(assets),
    ],
    emptyId,
  ];
}
